import {useForm} from "react-hook-form";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import FileUploader from "../../../../../util/component/FileUploader";
import CloseIcon from "@mui/icons-material/Close";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {WarningRounded} from "@mui/icons-material";
import {useAmexReportsRepository} from "../../../api/repositories/AmexReportsRepository";
import {useResetRecoilState} from "recoil";
import {STATE_OUTCOMES_UPLOADING} from "../constants/ReportRecoil";

const ResultsFile = (props: any) => {
    const {t} = useTranslation("finance", {keyPrefix: "payments.amex.transactions"});
    const {t: tg} = useTranslation("translation");
    const { uploadFile} = useAmexReportsRepository();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const resetUpload = useResetRecoilState(STATE_OUTCOMES_UPLOADING);
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>([]);

    const schema = Yup.object().shape({
        legalEntity: Yup.string().required(t('modal.messages.requiredCheck')),
        dealer: Yup.string().required(t('modal.messages.requiredCheck')),
    });
    const [errorMessageBox, showErrorMessageBox] = useState<boolean>(false);
    const defaultValues = () => {
        return {
            legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
            dealer: ""
        }
    };

    const {watch, control, reset} = useForm<any>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues()
    });


    const onSubmit = (file: File) => {

        let fileExtension = file.name.split('.').pop();
        if (fileExtension!.toLowerCase() === "csv") {
            uploadFile(
                file, watch("legalEntity"), watch("dealer")
            ).then((response) => {
                if(response) {
                    props.onClose();
                    resetUpload();
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("snackbar.success-upload", {entriesSize: response.result.entriesSize, reportSize: response.result.reportSize}),
                                type: response.result.entriesSize === response.result.reportSize  ? snackbarTypes.success : snackbarTypes.warning,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            }).catch(() => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.error-upload'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            });
            props.onClose();
            reset(defaultValues());
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-csv'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
            props.onClose();
            reset(defaultValues());
        }
    }

    const handleChangeLegalEntities = (value: any) => {
        console.log("handleChangeLegalEntities", value)
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        if (legalEntityOptions.length == 1 && dealers.length == 0) {
            setTimeout(() => {
                handleChangeLegalEntities(legalEntityOptions[0].value)
            });
        }
    })

    return (
        <Dialog fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {t('buttons.upload')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.onClose();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                    <Grid item xs={6}>
                        <FormSelect
                            name={"legalEntity"}
                            control={control}
                            props={{
                                label: tg("shared.legalEntities"),
                                fullWidth: true,
                                required: true,
                                onChange: (e: any) => {
                                    handleChangeLegalEntities(e.target.value)
                                },
                            }}
                            items={legalEntityOptions.map((option: any) => (
                                <MenuItem key={option.value}
                                          value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormSelect
                            name={"dealer"}
                            control={control}
                            props={{
                                label: tg("shared.dealers"),
                                fullWidth: true,
                                required: true,
                                disabled: dealers.length == 0
                            }}
                            items={dealers.map((option: any) => (
                                <MenuItem key={option.value}
                                          value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FileUploader
                            disabled={watch("legalEntity") === "" || watch("dealer") === ""}
                            errorMessage={t('modal.handleErrors.wrongFileFormat')}
                            onUpload={(file: File) => {
                                onSubmit(file)
                            }}
                            acceptedFiles={{'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.csv']}}/>
                    </Grid>
                    {errorMessageBox &&
                        <Grid item xs={12}>
                            <Box style={{display: 'flex', gap: '1ch'}}>
                                <WarningRounded/>
                                <TextField
                                    color={"error"}
                                    fullWidth
                                    id="error"
                                    disabled={true}
                                    value={t("handleErrors.wrongFileFormat")}
                                    type="text"
                                    multiline
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                />
                            </Box>
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "10px"}}>
                <Button color={"accent"}
                        onClick={() => {
                            reset(defaultValues());
                            props.onClose();
                        }}>{t('buttons.close')}</Button>
            </DialogActions>

        </Dialog>
    )
}

export default ResultsFile;

